import { useCallback, useEffect, useState } from "react";
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  Dialog,
  EmptyState,
  Input,
  PageHeader,
  Select,
  SkeletonRows,
  Table,
  Textarea,
  useToast,
  type Column,
} from "../../components/ui";
import { billingService, type Plan, type PlanInput } from "../../services/billing.service";
import { formatMoney, formatNumber } from "../../lib/format";


function limitLabel(value: number | null | undefined): string {
  return value === null || value === undefined ? "Ilimitado" : formatNumber(value);
}

const emptyForm: PlanInput = {
  name: "",
  description: "",
  monthly_price_cents: 0,
  yearly_price_cents: 0,
  annual_discount_percent: 0,
  trial_days: 14,
  limits: { projects: 3, ai_runs: 200, credits: 1000, storage_mb: 2048, deploys: 10, sandboxes: 1 },
  visibility: "public",
  is_active: true,
  is_mandatory: false,
  price_locked: false,
};

export default function PlansPage() {
  const toast = useToast();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Plan | null>(null);
  const [form, setForm] = useState<PlanInput>(emptyForm);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState<Plan | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await billingService.plans();
      setPlans(response.data);
    } catch {
      toast.error("Não foi possível carregar os planos.");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void load();
  }, [load]);

  function startCreate() {
    setEditing(null);
    setForm(emptyForm);
    setOpen(true);
  }

  function startEdit(plan: Plan) {
    setEditing(plan);
    setForm({
      name: plan.name,
      description: plan.description ?? "",
      monthly_price_cents: plan.monthly_price_cents,
      yearly_price_cents: plan.yearly_price_cents,
      annual_discount_percent: plan.annual_discount_percent,
      trial_days: plan.trial_days,
      limits: plan.limits,
      visibility: plan.visibility,
      is_active: plan.is_active,
      is_mandatory: plan.is_mandatory,
      price_locked: plan.price_locked,
      min_resale_price_cents: plan.min_resale_price_cents,
      max_resale_price_cents: plan.max_resale_price_cents,
    });
    setOpen(true);
  }

  async function save() {
    setSaving(true);
    try {
      if (editing) {
        await billingService.updatePlan(editing.id, form);
        toast.success("Plano atualizado.");
      } else {
        await billingService.createPlan(form);
        toast.success("Plano criado.");
      }
      setOpen(false);
      await load();
    } catch {
      toast.error("Não foi possível salvar o plano.");
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    if (!removing) return;
    try {
      await billingService.deletePlan(removing.id);
      toast.success("Plano removido.");
      await load();
    } catch {
      toast.error("Não foi possível remover. Verifique se há assinaturas ativas.");
    } finally {
      setRemoving(null);
    }
  }

  const columns: Column<Plan>[] = [
    {
      key: "name",
      header: "Plano",
      render: (plan) => (
        <div>
          <div className="font-medium text-ink">{plan.name}</div>
          <div className="text-xs text-ink-muted">{plan.is_global ? "Global" : "Próprio"}</div>
        </div>
      ),
    },
    { key: "monthly", header: "Mensal", render: (plan) => formatMoney(plan.monthly_price_cents) },
    {
      key: "yearly",
      header: "Anual",
      hideOnMobile: true,
      render: (plan) => (
        <span>
          {formatMoney(plan.yearly_price_cents)}
          {plan.annual_discount_percent > 0 && (
            <span className="ml-1 text-xs text-ink-muted">-{plan.annual_discount_percent}%</span>
          )}
        </span>
      ),
    },
    {
      key: "limits",
      header: "Limites",
      hideOnMobile: true,
      render: (plan) => (
        <span className="text-xs text-ink-muted">
          {limitLabel(plan.limits.projects)} projetos · {limitLabel(plan.limits.ai_runs)} IA ·{" "}
          {limitLabel(plan.limits.credits)} créditos
        </span>
      ),
    },
    {
      key: "flags",
      header: "Situação",
      render: (plan) => (
        <div className="flex flex-wrap gap-1">
          <Badge tone={plan.is_active ? "ok" : "neutral"}>{plan.is_active ? "Ativo" : "Inativo"}</Badge>
          {plan.is_mandatory && <Badge tone="info">Obrigatório</Badge>}
          {plan.price_locked && <Badge tone="warn">Preço travado</Badge>}
        </div>
      ),
    },
    {
      key: "actions",
      header: "",
      align: "right",
      render: (plan) => (
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="ghost" onClick={() => startEdit(plan)}>
            Editar
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setRemoving(plan)}>
            Remover
          </Button>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Planos"
        description="Preços, limites e recursos vendidos pelo Builder. Planos globais valem para toda a hierarquia."
        actions={<Button onClick={startCreate}>Novo plano</Button>}
      />

      <Card>
        {loading ? (
          <SkeletonRows rows={4} />
        ) : (
          <Table
            columns={columns}
            rows={plans}
            rowKey={(plan) => plan.id}
            empty={
              <EmptyState
                title="Nenhum plano cadastrado"
                description="Crie o primeiro plano para começar a vender assinaturas."
              />
            }
          />
        )}
      </Card>

      <Dialog
        open={open}
        title={editing ? "Editar plano" : "Novo plano"}
        onClose={() => setOpen(false)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={save} loading={saving}>
              Salvar
            </Button>
          </>
        }
      >
        <div className="grid gap-3 sm:grid-cols-2">
          <Input
            label="Nome"
            value={form.name}
            onChange={(event) => setForm({ ...form, name: event.target.value })}
          />
          <Select
            label="Visibilidade"
            value={form.visibility ?? "public"}
            onChange={(event) => setForm({ ...form, visibility: event.target.value as "public" | "private" })}
            options={[
              { value: "public", label: "Pública" },
              { value: "private", label: "Privada" },
            ]}
          />
          <div className="sm:col-span-2">
            <Textarea
              label="Descrição"
              rows={2}
              value={form.description ?? ""}
              onChange={(event) => setForm({ ...form, description: event.target.value })}
            />
          </div>
          <Input
            label="Mensalidade (centavos)"
            type="number"
            value={String(form.monthly_price_cents)}
            onChange={(event) => setForm({ ...form, monthly_price_cents: Number(event.target.value) })}
          />
          <Input
            label="Anualidade (centavos)"
            type="number"
            value={String(form.yearly_price_cents)}
            onChange={(event) => setForm({ ...form, yearly_price_cents: Number(event.target.value) })}
          />
          <Input
            label="Desconto anual (%)"
            type="number"
            value={String(form.annual_discount_percent ?? 0)}
            onChange={(event) => setForm({ ...form, annual_discount_percent: Number(event.target.value) })}
          />
          <Input
            label="Dias de teste"
            type="number"
            value={String(form.trial_days ?? 0)}
            onChange={(event) => setForm({ ...form, trial_days: Number(event.target.value) })}
          />

          {(
            [
              ["projects", "Projetos"],
              ["ai_runs", "Execuções de IA / mês"],
              ["credits", "Créditos / mês"],
              ["storage_mb", "Armazenamento (MB)"],
              ["deploys", "Publicações / mês"],
              ["sandboxes", "Ambientes simultâneos"],
            ] as const
          ).map(([key, label]) => (
            <Input
              key={key}
              label={label}
              type="number"
              hint="Vazio = ilimitado"
              value={form.limits?.[key] === null || form.limits?.[key] === undefined ? "" : String(form.limits[key])}
              onChange={(event) =>
                setForm({
                  ...form,
                  limits: {
                    ...form.limits,
                    [key]: event.target.value === "" ? null : Number(event.target.value),
                  },
                })
              }
            />
          ))}

          <label className="flex items-center gap-2 text-sm text-ink-muted">
            <input
              type="checkbox"
              checked={form.is_mandatory ?? false}
              onChange={(event) => setForm({ ...form, is_mandatory: event.target.checked })}
            />
            Obrigatório para os níveis abaixo
          </label>
          <label className="flex items-center gap-2 text-sm text-ink-muted">
            <input
              type="checkbox"
              checked={form.price_locked ?? false}
              onChange={(event) => setForm({ ...form, price_locked: event.target.checked })}
            />
            Preço travado na revenda
          </label>
        </div>
      </Dialog>

      <ConfirmDialog
        open={removing !== null}
        title="Remover plano"
        description={`O plano "${removing?.name ?? ""}" deixará de ser oferecido.`}
        confirmLabel="Remover"
        onConfirm={remove}
        onCancel={() => setRemoving(null)}
      />
    </>
  );
}
